/**
 * Reactive user formatting preferences (Perfil → Formato de Fecha / Formato de Números).
 *
 * Why this exists: the profile form persists `preferences.dateFormat` and
 * `preferences.numberFormat` to the user record, but nothing consumed them —
 * the controls were decorative (dates rendered es-DO long-form everywhere,
 * numbers always point-decimal regardless of the saved choice).
 *
 * The auth store calls `syncFormatPreferences` every time the user object is
 * (re)hydrated — login, getProfile, updateProfile, updatePreferences — so the
 * refs below are the single reactive source the formatting layer reads.
 * Plain module refs instead of a Pinia store to stay import-cycle-free:
 * `utils/dateFormat` and `composables/useCurrency` need this, and both are
 * imported from stores.
 *
 * Vocabulary note: `dateFormat` uses the same tokens as tenant settings
 * (`DD/MM/YYYY` | `MM/DD/YYYY` | `YYYY-MM-DD`); `numberFormat` uses the
 * profile form's values (`decimal-point` | `decimal-comma`), NOT an Intl
 * locale — callers must not feed it to `Intl.NumberFormat` directly.
 */

import { ref } from 'vue'

export const DATE_PATTERNS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'] as const
export const NUMBER_FORMATS = ['decimal-point', 'decimal-comma'] as const

export type DateFormatPattern = (typeof DATE_PATTERNS)[number]
export type NumberFormatPreference = (typeof NUMBER_FORMATS)[number]

/** 'DD/MM/YYYY' is the app default: formatDate keeps its es-DO rendering for it. */
export const dateFormatPreference = ref<DateFormatPattern>('DD/MM/YYYY')
export const numberFormatPreference = ref<NumberFormatPreference>('decimal-point')

/**
 * Mirror the user's saved preferences into the reactive refs. Unknown/absent
 * values keep the current one (fail-safe towards the app default rendering).
 */
export function syncFormatPreferences(
  preferences?: { dateFormat?: string | null; numberFormat?: string | null } | null
): void {
  if (!preferences) return
  if (preferences.dateFormat && (DATE_PATTERNS as readonly string[]).includes(preferences.dateFormat)) {
    dateFormatPreference.value = preferences.dateFormat as DateFormatPattern
  }
  if (preferences.numberFormat && (NUMBER_FORMATS as readonly string[]).includes(preferences.numberFormat)) {
    numberFormatPreference.value = preferences.numberFormat as NumberFormatPreference
  }
}

/** Reset to app defaults (used on logout and by tests). */
export function resetFormatPreferences(): void {
  dateFormatPreference.value = 'DD/MM/YYYY'
  numberFormatPreference.value = 'decimal-point'
}

/**
 * Rewrite the decimal separator of an already-Intl-formatted number string.
 * 'decimal-point' is a no-op (es-DO/en-US already render point decimals);
 * 'decimal-comma' swaps both separators ("RD$5,900.00" → "RD$5.900,00").
 * A sentinel avoids clobbering when both separators appear.
 */
export function applyDecimalSeparatorPreference(formatted: string): string {
  if (numberFormatPreference.value !== 'decimal-comma') return formatted
  const sentinel = String.fromCharCode(1)
  return formatted
    .replace(/\./g, sentinel)
    .replace(/,/g, '.')
    .replace(new RegExp(sentinel, 'g'), ',')
}
